import { AnyAction, createAction, createAsyncThunk, createSlice, ThunkDispatch } from '@reduxjs/toolkit';
import { t } from "i18next";
import axios from '../../config/axios';
import Toast from '../../shared/toast/Toast';
import { ApplicantsListRequest } from '../../utils/interfaces/applicants/ApplicantsListRequest';
import { ApplicantsListResponse } from '../../utils/interfaces/applicants/ApplicantsListResponse';
import { ResponseResult } from '../../utils/interfaces/http/ResponseResult';
import { VettingTabsEnum } from 'src/utils/enums/VettingTabsEnum';
import { ApplicantNoteItem, ApplicantsFiltersEnumObject, ApplicantsToMoveBetweenKanbanColumns, ListApplicantNotesRequestParam, LocalFilterEnum, UpsertApplicantNoteRequestParam, ManageApplicantRejectionReasonsRequestBody, VettingStoreInferface } from 'src/utils/interfaces/vetting/VettingStoreInferface';

import { AiInterviewStatusEnum, ApplicantListItem, DynamicKanbanColumn, DynamicApplicationStatus } from 'src/utils/interfaces/applicants/ApplicantListItem';
import { VettingDetailsTabsEnum } from 'src/utils/enums/VettingDetailsTabsEnum';
import { FULLTIME_PARTNER_ID_KEY, MASS_HIRING_APPLICANTS__KANBAN_PAGE_LIMIT, MASS_HIRING_APPLICANTS_PAGE_LIMIT, VETTING_APPLICANTS_PAGE_LIMIT } from 'src/utils/constants/constants';
import { ApplicantsFilter } from 'src/utils/interfaces/applicants/ApplicantsFilter';
import { ApplicantsSortByEnum } from 'src/utils/enums/ApplicantsSortByEnum';
import { ApplicationStatusEnum } from 'src/utils/enums/ApplicationStatus';
import { UpdateApplicantsData } from 'src/utils/interfaces/applicants/UpdateApplicantsData';
import { UnlockContactInfoRequest } from 'src/utils/interfaces/job/QueryParams';
import { getPartnerInfo } from '../partner/partner';
import { getJobDetails } from '../jobs/job';
import { getApiResponseFromCache, invalidateJobPostCache, removeDuplicatesFromArrayOfObjects2, setApiResponseInCache, updateObjectInList, updateObjectsInList } from 'src/utils/helpers/utils';
import { trackAiInterviewRequested, getPartnerMarketingContext } from 'src/utils/google-analytics/marketing';
import { captureMessage } from '@sentry/browser';
import { SubscriptionPlanCategoryEnum } from 'src/utils/enums/PartnerSubscription';
import { FullTimeJobDetails } from 'src/utils/interfaces/job/JobDetails';
import { GenderEnum } from 'src/utils/enums/Gender';
import { BulkMessageRequest } from 'src/utils/interfaces/mass-hiring/MassHiringInterfaces';
import { mockApplicantsResponse } from 'src/utils/constants/mockApplicants';
import { ListApplicationStagesItem, UpdateApplicationStagesBody } from 'src/utils/interfaces/job/Job.store';

const appKey = 'appVetting'
const apiModuleUrl = 'pa/ft/job-applications';
const jobDetailsUrl = 'pa/ft/job-posts';
const vettingUrl = 'pa/ft/ai-interviews';
const apiDynamicStagesUrl = `pa/ft/partner-application-stages`;


export const getVettingApplicantsList = createAsyncThunk(`${appKey}/getVettingApplicantsList`, async (data: ApplicantsListRequest, { dispatch }): Promise<ApplicantsListResponse> => {
    const apiUrl = `${apiModuleUrl}/${data.jobId}/list`;;
    const queryParams = { ...data.filters } as any;
    delete queryParams.page;
    if (data.isMassHiring) {
        queryParams.limit = MASS_HIRING_APPLICANTS_PAGE_LIMIT;
    }

    if (data.isMassHiring && (!queryParams.statuses || queryParams.statuses?.length === 0)) {
        queryParams.statuses = [ApplicationStatusEnum.PENDING, ApplicationStatusEnum.SHORT_LISTED, ApplicationStatusEnum.FINALIST, ApplicationStatusEnum.CUSTOM, ApplicationStatusEnum.INTERVIEWED_BY_SAUD, ApplicationStatusEnum.ONBOARDING];
    }

    if (queryParams.searchString) {
        if (queryParams.searchString?.startsWith('+')) {
            queryParams.searchString = queryParams.searchString?.replace('+', '');
        }
    } else {
        delete queryParams.searchString;
    }

    const cacheData: ApplicantsListResponse = getApiResponseFromCache(apiUrl, queryParams);
    if (cacheData) {
        if (!data.isMassHiring) {
            dispatch(prefetchNextApplicantsPageIfNeeded({ applicantsData: cacheData, queryParams, jobId: data.jobId }));
        }
        return new Promise((resolve: any) => {
            setTimeout(() => {
                resolve({ ...cacheData, fromCache: true });
            }, 200);
        });
    }

    const { screeningQuestionFilters, ...restQueryParams } = queryParams;
    let requestUrl = apiUrl;
    if (screeningQuestionFilters?.length) {
        const sqParts: string[] = [];
        screeningQuestionFilters.forEach((filter: any, index: number) => {
            sqParts.push(`screeningQuestionFilters[${index}][questionExternalId]=${filter.questionExternalId}`);
            filter.selectedOptionIds?.forEach((optionId: string) => {
                sqParts.push(`screeningQuestionFilters[${index}][selectedOptionIds][]=${optionId}`);
            });
        });
        requestUrl = `${apiUrl}?${sqParts.join('&')}`;
    }

    const response = await axios.get<ResponseResult<ApplicantsListResponse>>(requestUrl, { params: restQueryParams });
    const applicantsData: ApplicantsListResponse = response.data.data;

    setApiResponseInCache(apiUrl, queryParams, { ...applicantsData, jobId: data.jobId });
    if (applicantsData?.applicants?.length >= VETTING_APPLICANTS_PAGE_LIMIT && !data.isMassHiring) {
        dispatch(prefetchNextApplicantsPageIfNeeded({ applicantsData, queryParams, jobId: data.jobId }));
    }

    return { ...applicantsData, fromCache: false };
});

export const prefetchNextApplicantsPageIfNeeded = createAsyncThunk(`${appKey}/prefetchNextApplicantsPageIfNeeded`, async (params: { applicantsData: ApplicantsListResponse, queryParams: ApplicantsFilter, jobId: string }) => {
    const apiUrl = `${apiModuleUrl}/${params.jobId}/list`;
    const { applicantsData, queryParams } = params;
    const newQueryParams = { ...queryParams, skip: queryParams.skip + VETTING_APPLICANTS_PAGE_LIMIT };
    const cacheData = getApiResponseFromCache(apiUrl, newQueryParams);

    if (applicantsData?.total > VETTING_APPLICANTS_PAGE_LIMIT && !cacheData) {
        const response = await axios.get(apiUrl, { params: newQueryParams });
        const applicantsData2: ApplicantsListResponse = response.data.data;
        setApiResponseInCache(apiUrl, newQueryParams, { ...applicantsData2, jobId: params.jobId });
        return { ...applicantsData2, fromCache: false }
    }

    return { viewsRemaining: cacheData?.viewsRemaining || null, fromCache: true };
});

export const getKanbanColumns = createAsyncThunk(`${appKey}/getKanbanColumns`, async (jobId: string): Promise<DynamicKanbanColumn[]> => {
    const response = await axios.get(`${apiDynamicStagesUrl}/list?jobId=${jobId}`);

    return response.data.data?.map((stage: ListApplicationStagesItem) => ({
        id: stage.id,
        title: stage.name,
        order: stage.sortIndex,
        status: stage.enum,
    })) as DynamicKanbanColumn[] || [];
});

export const getApplicationStatuses = createAsyncThunk(`${appKey}/getApplicationStatuses`, async (jobId: string): Promise<DynamicApplicationStatus[]> => {
    const response = await axios.get(`${apiDynamicStagesUrl}/list?jobId=${jobId}`);
    const stages = response.data.data as ListApplicationStagesItem[];
    stages.push({
        id: ApplicationStatusEnum.NOT_SELECTED,
        name: t('applicant_status_NOT_SELECTED'),
        enum: ApplicationStatusEnum.NOT_SELECTED,
        sortIndex: 99999999,
    })
    return stages?.map((stage: ListApplicationStagesItem) => ({
        id: stage.id,
        value: stage.id,
        label: stage.name,
        order: stage.sortIndex,
        enum: stage.enum,
        isRejection: stage.enum === ApplicationStatusEnum.NOT_SELECTED,
    })) as DynamicApplicationStatus[] || [];
});

export const getVettingApplicantsListForKanbanView = createAsyncThunk(`${appKey}/getVettingApplicantsListForKanbanView`, async (data: ApplicantsListRequest & { columnId?: string }, { dispatch }): Promise<ApplicantsListResponse & { columnId?: string; newStatus?: ApplicationStatusEnum; isVettedBySaud?: boolean }> => {
    const apiUrl = `${apiModuleUrl}/${data.jobId}/list`;;
    const queryParams = { ...data.filters } as any;
    delete queryParams.page;
    queryParams.limit = MASS_HIRING_APPLICANTS__KANBAN_PAGE_LIMIT;
    const newStatus = data?.filters?.statuses?.[0] || ApplicationStatusEnum.PENDING;
    const isVettedBySaud = data?.filters?.vettedBySaud;

    if (queryParams.searchString) { if (queryParams.searchString?.startsWith('+')) { queryParams.searchString = queryParams.searchString?.replace('+', ''); } } else { delete queryParams.searchString; }

    const cacheData: ApplicantsListResponse = getApiResponseFromCache(apiUrl, queryParams);
    if (cacheData) {
        return new Promise((resolve: any) => {
            setTimeout(() => {
                resolve({ ...cacheData, fromCache: true, newStatus, isVettedBySaud, columnId: data.columnId });
            }, 200);
        });
    }
    const response = await axios.get<ResponseResult<ApplicantsListResponse>>(apiUrl, { params: queryParams });
    const applicantsData: ApplicantsListResponse = response.data.data;
    setApiResponseInCache(apiUrl, queryParams, { ...applicantsData, jobId: data.jobId });
    return { ...applicantsData, fromCache: false, newStatus, isVettedBySaud, columnId: data.columnId };
});

export const getApplicantDetails = createAsyncThunk(`${appKey}/getApplicant`, async (query: { applicationId: string, isOffline?: boolean, skipViewsDecrement?: boolean }) => {
    const response = await axios.get<ResponseResult<ApplicantListItem>>(`${apiModuleUrl}/${query.applicationId}/details?isOffline=${query.isOffline || false}`);
    return response.data.data;
})

export const getApplicantDetailsAndUnlock = createAsyncThunk(`${appKey}/getApplicantDetailsAndUnlock`, async (query: UnlockContactInfoRequest, { dispatch }) => {
    const response = await axios.get<ResponseResult<ApplicantListItem>>(`${apiModuleUrl}/${query.applicantId}/details?isOffline=${query.isOffline || false}`);
    const res1Data = response?.data?.data;

    const body = {
        currentPage: query.currentPage,
        applicantRankInPage: query.applicantRankInPage,
        sortMethod: query.sortMethod
    }

    const endPoint = `${jobDetailsUrl}/${query.jobId}/unlock/${query.applicantId}?isOffline=${query.isOffline || false}`
    const response2 = await axios.put<ResponseResult<any>>(endPoint, body);
    const res2Data = response2?.data?.data;

    // if (response2.status === 200) { dispatch(getPartnerInfo()); }

    return { ...res1Data, ...res2Data }
})

export const updateSingleApplicantStatus = createAsyncThunk(
    `${appKey}/updateSingleApplicantStatus`,
    async (updatedApplicantsData: UpdateApplicantsData, { rejectWithValue }) => {
        try {
            const response = await axios.put(`${apiModuleUrl}/update/status`, updatedApplicantsData);
            return {
                ...response.data.data,
                status: updatedApplicantsData.status,
                originalStatus: updatedApplicantsData.originalStatus,
                isMassHiring: updatedApplicantsData.isMassHiring,
                isKanbanView: updatedApplicantsData.isKanbanView,
                isRejectedTabActive: updatedApplicantsData.isRejectedTabActive,
                isInterviewed: updatedApplicantsData.isInterviewed,
                newStageExternalId: updatedApplicantsData.newStageExternalId,
                sourceColumnId: updatedApplicantsData.sourceColumnId,
                destinationColumnId: updatedApplicantsData.destinationColumnId
            };
        } catch (error: any) {
            return rejectWithValue({
                ...error,
                jobApplicationIds: updatedApplicantsData.jobApplicationIds || [],
                offlineJobApplicationIds: updatedApplicantsData.offlineJobApplicationIds || [],
            });
        }
    },
);

export const updateMultipleApplicantsStatus = createAsyncThunk(
    `${appKey}/updateMultipleApplicantStatus`,
    async (updatedApplicantsData: UpdateApplicantsData, { rejectWithValue }) => {
        try {
            const response = await axios.put(`${apiModuleUrl}/update/status`, updatedApplicantsData);
            return {
                ...response.data.data,
                status: updatedApplicantsData.status,
                originalStatus: updatedApplicantsData.originalStatus,
                isMassHiring: updatedApplicantsData.isMassHiring,
                isKanbanView: updatedApplicantsData.isKanbanView,
                isRejectedTabActive: updatedApplicantsData.isRejectedTabActive,
                newStageExternalId: updatedApplicantsData.newStageExternalId,
                sourceColumnId: updatedApplicantsData.sourceColumnId,
                destinationColumnId: updatedApplicantsData.destinationColumnId
            };
        } catch (error: any) {
            return rejectWithValue({
                ...error,
                jobApplicationIds: updatedApplicantsData.jobApplicationIds || [],
                offlineJobApplicationIds: updatedApplicantsData.offlineJobApplicationIds || [],
            });
        }
    },
);

export interface SyncToTalentAcquisitionRequest {
    jobApplicationIds: string[];
    offlineJobApplicationIds: string[];
}

export interface SyncToTalentAcquisitionResult {
    total: number;
    submittedCount: number;
    successCount: number;
    failedCount: number;
    skippedCount: number;
    results: Array<{
        applicationId: string;
        isOffline: boolean;
        email?: string;
        success: boolean;
        skipped: boolean;
        skipReason?: string;
        applicationNum?: string;
        error?: string;
    }>;
}

export const syncApplicantsToTalentAcquisition = createAsyncThunk(
    `${appKey}/syncApplicantsToTalentAcquisition`,
    async (data: SyncToTalentAcquisitionRequest, { rejectWithValue }) => {
        try {
            const response = await axios.post<ResponseResult<SyncToTalentAcquisitionResult>>(
                `${apiModuleUrl}/sync-to-talent-acquisition`,
                data,
            );
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error);
        }
    },
);

export const updateSingleApplicantStatusWithoutState = createAsyncThunk(`${appKey}/updateApplicantsStatusesWithoutState`, async (data: { applicantId: string, isOffline?: boolean, isAudio: boolean, isKanbanView?: boolean }) => {
    const updateStatusBody: UpdateApplicantsData = {
        status: ApplicationStatusEnum.INTERVIEWED_BY_SAUD,
        jobApplicationIds: data.isOffline ? [] : [data.applicantId],
        offlineJobApplicationIds: data.isOffline ? [data.applicantId] : [],
        isKanbanView: data.isKanbanView || false
    };
    const response = await axios.put(`${apiModuleUrl}/update/status`, updateStatusBody);
    return response.data.data;
});

export const updateApplicantsStatusesWithoutState = createAsyncThunk(`${appKey}/updateApplicantsStatusesWithoutState`, async (data: { applicantsIds: string[], offlineJobApplicationIds: string[], isAudio: boolean, isKanbanView?: boolean, pendingApplicantsIds?: string[] }) => {
    const updateStatusBody: UpdateApplicantsData = {
        status: ApplicationStatusEnum.INTERVIEWED_BY_SAUD,
        jobApplicationIds: data.applicantsIds || [],
        offlineJobApplicationIds: data.offlineJobApplicationIds || [],
        isKanbanView: data.isKanbanView || false
    };

    const response = await axios.put(`${apiModuleUrl}/update/status`, updateStatusBody);
    return response.data.data;
});

export const unlockApplicantVetting = createAsyncThunk(`${appKey}/unlockContactInfo`, async (query: UnlockContactInfoRequest, { dispatch }) => {
    const body = {
        currentPage: query.currentPage,
        applicantRankInPage: query.applicantRankInPage,
        sortMethod: query.sortMethod
    }

    const endPoint = `${jobDetailsUrl}/${query.jobId}/unlock/${query.applicantId}?isOffline=${query.isOffline || false}`
    const response = await axios.put<ResponseResult<any>>(endPoint, body);

    if (response.status === 200) {
        dispatch(getPartnerInfo());
        // dispatch(getJobDetails({ jobId: query.jobId }))
    }

    return response.data.data;
})

const trackAiInterviewRequestedIfNeeded = (applicantId: string | undefined, isAudio: boolean) => {
    if (!applicantId) return;

    const { partner, partnerUser } = getPartnerMarketingContext();
    trackAiInterviewRequested({
        partner,
        partnerUser,
        candidateId: applicantId,
        interviewType: isAudio ? 'audio' : 'text',
    });
};

const trackBatchAiInterviewRequested = (data: {
    applicantsIds?: string[];
    offlineJobApplicationIds?: string[];
    isAudio: boolean;
}) => {
    const { partner, partnerUser } = getPartnerMarketingContext();
    const candidateIds = [...(data.applicantsIds ?? []), ...(data.offlineJobApplicationIds ?? [])].filter(Boolean);

    candidateIds.forEach((candidateId) => {
        trackAiInterviewRequested({
            partner,
            partnerUser,
            candidateId,
            interviewType: data.isAudio ? 'audio' : 'text',
        });
    });
};

export const vetApplicant = createAsyncThunk(`${appKey}/vetApplicant`, async (data: { applicantId: string, isOffline?: boolean, isAudio: boolean, isKanbanView?: boolean }, { dispatch }) => {
    const response = await axios.post(`${vettingUrl}/bulk-invite${data.isAudio ? '?isAudio=true' : ''}`, { jobApplicationIds: data.isOffline ? [] : [data.applicantId], offlineJobApplicationIds: data.isOffline ? [data.applicantId] : [] });
    // dispatch(updateSingleApplicantStatusWithoutState(data));
    trackAiInterviewRequestedIfNeeded(data.applicantId, data.isAudio);
    return { ...response.data.data, isKanbanView: data.isKanbanView || false };
});


export const vetApplicantsBatch = createAsyncThunk(`${appKey}/vetApplicantBatch`, async (data: { applicantsIds: string[], offlineJobApplicationIds: string[], isAudio: boolean, isKanbanView?: boolean, pendingApplicantsIds?: string[] }, { dispatch }) => {
    const response = await axios.post(`${vettingUrl}/bulk-invite${data.isAudio ? '?isAudio=true' : ''}`, { jobApplicationIds: data.applicantsIds || [], offlineJobApplicationIds: data.offlineJobApplicationIds || [] });
    // dispatch(updateApplicantsStatusesWithoutState(data));
    trackBatchAiInterviewRequested(data);
    return { ...response.data.data, jobApplicationIds: data.applicantsIds, isKanbanView: data.isKanbanView || false, pendingApplicantsIds: data.pendingApplicantsIds || [] };
});

export const fetchVideoInterview = createAsyncThunk(`${appKey}/fetchVideoInterview`, async (data: { applicantId: string }) => {
    const response = await axios.get(`${apiModuleUrl}/${data.applicantId}/video-cv`);
    return response.data.data;
});

export const cancelInterviews = createAsyncThunk(`${appKey}/cancelInterviews`, async (jobId: string) => {
    const response = await axios.delete(`${vettingUrl}/cancel-pending/${jobId}`);
    return response.data.data;
});

export const rateInterview = createAsyncThunk(`${appKey}/rateInterview`, async (data: { interviewId: string, rate: number }) => {
    const response = await axios.post(`${vettingUrl}/feedback/${data.interviewId}`, { rate: data.rate });
    return response.data.data;
});

export const getInterviewDetails = createAsyncThunk(`${appKey}/getInterviewDetails`, async (interviewId: string) => {
    const response = await axios.get(`${vettingUrl}/log/${interviewId}?skip=0&limit=100`);
    return response.data.data;
})

export const sendBulkMessage = createAsyncThunk(`${appKey}/sendBulkMessage`, async (data: BulkMessageRequest) => {
    const response = await axios.post(`pa/ft/chat/send-bulk-chat-msg`, data);
    return response.data.data
});

export const sendDataCollectionRequests = createAsyncThunk(`${appKey}/sendDataCollectionRequests`, async (data: { applicantsIds?: string[], offlineApplicantsIds?: string[], jobPostExternalId: string }, { dispatch, getState }) => {
    const requestBody = {
        ...(data.applicantsIds && data.applicantsIds.length > 0 && { jobApplicationExternalIds: data.applicantsIds }),
        ...(data.offlineApplicantsIds && data.offlineApplicantsIds.length > 0 && { offlineJobApplicationExternalIds: data.offlineApplicantsIds }),
        jobPostExternalId: data.jobPostExternalId
    };
    
    const response = await axios.post(`pa/ft/data-collection/requests/send`, requestBody);
    
    const state = getState() as any;
    const vettingState = state.vetting;
    
    dispatch(getVettingApplicantsList({
        jobId: data.jobPostExternalId,
        filters: vettingState.filters || {},
        isMassHiring: vettingState.isMassHiring || false
    }));
    
    return response.data.data;
});

export const cancelDataCollectionRequests = createAsyncThunk(`${appKey}/cancelDataCollectionRequests`, async (data: { requestExternalIds: string[], reason: string }, { dispatch, getState }) => {
    const response = await axios.post(`pa/ft/data-collection/requests/cancel`, data);
    const state = getState() as any;
    const vettingState = state.vetting;
    dispatch(getVettingApplicantsList({
        jobId: vettingState.jobId,
        filters: vettingState.filters || {},
        isMassHiring: vettingState.isMassHiring || false
    }));
    return response.data.data;
});

export const resendDataCollectionRequests = createAsyncThunk(`${appKey}/resendDataCollectionRequests`, async (data: { requestExternalIds: string[] }, { dispatch, getState }) => {
    const response = await axios.post(`pa/ft/data-collection/requests/resend`, data);
    const state = getState() as any;
    const vettingState = state.vetting;
    dispatch(getVettingApplicantsList({
        jobId: vettingState.jobId,
        filters: vettingState.filters || {},
        isMassHiring: vettingState.isMassHiring || false
    }));
    return response.data.data;
});

export const recollectTask = createAsyncThunk(`${appKey}/recollectTask`, async (data: { requestExternalId: string; taskExternalId: string; reason: string }, { dispatch, getState }) => {
    const response = await axios.post(`pa/ft/data-collection/requests/${data.requestExternalId}/tasks/${data.taskExternalId}/recollect`, { reason: data.reason });
    const state = getState() as any;
    const vettingState = state.vetting;
    dispatch(getVettingApplicantsList({
        jobId: vettingState.jobId,
        filters: vettingState.filters || {},
        isMassHiring: vettingState.isMassHiring || false
    }));
    return response.data.data;
});

// Notes
export const getApplicantNotes = createAsyncThunk(`${appKey}/getApplicantNotes`, async (data: ListApplicantNotesRequestParam) => {
    if (!data.jobApplicationId && !data.workerExternalId) return [];
    const response = await axios.get(`pa/ft/applicant-notes/list`, { params: data });
    return response.data.data
});

export const createApplicantNote = createAsyncThunk(`${appKey}/createApplicantNote`, async (data: UpsertApplicantNoteRequestParam, { rejectWithValue }) => {
    try {
        const response = await axios.post(`pa/ft/applicant-notes`, { note: data.note }, { params: data });
        return { ...response.data.data, externalId: response.data.data.noteExternalId, note: data.note, createdAt: response.data.data.createdAt, ownerName: response.data.data.ownerName, ownerExternalId: response.data.data.ownerExternalId }
    } catch (error: any) {
        return rejectWithValue({ message: error?.message, code: error?.code });
    }
});

export const updateApplicantNote = createAsyncThunk(`${appKey}/updateApplicantNote`, async (data: UpsertApplicantNoteRequestParam, { rejectWithValue }) => {
    try {
        const response = await axios.put(`pa/ft/applicant-notes/${data.noteId}`, { note: data.note }, { params: data });
        return { ...response.data.data, externalId: response.data.data.noteExternalId, note: data.note, createdAt: response.data.data.createdAt, ownerName: response.data.data.ownerName, ownerExternalId: response.data.data.ownerExternalId }
    } catch (error: any) {
        return rejectWithValue({ message: error?.message, code: error?.code });
    }
});

export const deleteApplicantNote = createAsyncThunk(`${appKey}/deleteApplicantNote`, async (data: UpsertApplicantNoteRequestParam) => {
    const response = await axios.delete(`pa/ft/applicant-notes/${data.noteId}`, { params: data });
    return response.data.data
});


export const manageApplicantRejectionReason = createAsyncThunk(`${appKey}/manageApplicantRejectionReason`, async (data: ManageApplicantRejectionReasonsRequestBody) => {
    const response = await axios.put(`pa/ft/job-applications/manage-applicant-rejection-reasons`, data);
    return response.data.data
});

export const getJobDynamicStages = createAsyncThunk(`${appKey}/getJobDynamicStages`, async (jobId: string) => {
    const response = await axios.get(`${apiDynamicStagesUrl}/list?jobId=${jobId}`);
    return response.data.data as ListApplicationStagesItem[];
})

export const upsertJobDynamicStages = createAsyncThunk(`${appKey}/upsertJobDynamicStages`, async (data: { jobId: string, body: UpdateApplicationStagesBody }, { dispatch }) => {
    const response = await axios.put(`${apiDynamicStagesUrl}/job/${data.jobId}/upsert-stages`, data.body);
    dispatch(getApplicationStatuses(data.jobId));
    // Refetch so newly created stages get real server IDs (clears client-side isNew)
    await dispatch(getJobDynamicStages(data.jobId));
    return response.data.data;
})

export const deleteJobDynamicStage = createAsyncThunk(`${appKey}/deletePartnerDynamicStage`, async (data: { jobId: string, stageId: string, newStageId?: string }, { dispatch }) => {
    const response = await axios.delete(`${apiDynamicStagesUrl}/job/${data.jobId}/delete-stage/${data.stageId}`, {
        data: data.newStageId ? { newStageId: data.newStageId } : undefined
    });
    dispatch(getApplicationStatuses(data.jobId));
    return response.data.data;
})

export const setIsLoadingList = createAction<boolean>(`${appKey}/setIsLoadingList`);
export const setActiveVettingTab = createAction<VettingTabsEnum>(`${appKey}/setActiveVettingTab`);
export const setActiveVettingDetailsTab = createAction<VettingDetailsTabsEnum>(`${appKey}/setActiveVettingDetailsTab`);
export const setCurrentPage = createAction<number>(`${appKey}/setCurrentPage`);
export const updateSelectedApplicant = createAction<ApplicantListItem>(`${appKey}/updateSelectedApplicant`);
export const resetApplicantsPagination = createAction<boolean>(`${appKey}/resetApplicantsPagination`);
export const setIsFiltersMenuVisible = createAction<boolean>(`${appKey}/setIsFiltersMenuVisible`);
export const setFilters = createAction<ApplicantsFilter>(`${appKey}/setFilters`);
export const setSelectedFilters = createAction<ApplicantsFiltersEnumObject>(`${appKey}/setSelectedFilters`);
export const setSelectedFiltersRow = createAction<ApplicantsFiltersEnumObject>(`${appKey}/setSelectedFiltersRow`);
export const setSelectedApplicants = createAction<ApplicantListItem[]>(`${appKey}/setSelectedApplicants`);
export const setFilterErrors = createAction<ApplicantsFiltersEnumObject>(`${appKey}/setFilterErrors`);
export const setShowSubscriptionModal = createAction<boolean>(`${appKey}/setShowSubscriptionModal`);
export const setShowCloseJobModal = createAction<boolean>(`${appKey}/setShowCloseJobModal`);
export const setShowReopenJobModal = createAction<boolean>(`${appKey}/setShowReopenJobModal`);
export const setShowConsumedJobPostModal = createAction<boolean>(`${appKey}/setShowConsumedJobPostModal`);
export const setShowInterviewMethodModal = createAction<boolean>(`${appKey}/setShowInterviewMethodModal`);
export const setShowUnlocksConsumedModal = createAction<boolean>(`${appKey}/setShowUnlocksConsumedModal`);
export const setShowRecommendationEducationalModal = createAction<boolean>(`${appKey}/setShowRecommendationEducationalModal`);
export const setShowCancelInterviewsModal = createAction<boolean>(`${appKey}/setShowCancelInterviewsModal`);
export const setIsBackendRecommending = createAction<boolean>(`${appKey}/setIsBackendRecommending`);
export const updateRecentlyViewedApplicants = createAction<string[]>(`${appKey}/updateRecentlyViewedApplicants`);
export const setIsFiltersReset = createAction<boolean>(`${appKey}/setIsFiltersReset`);
export const setJobId = createAction<string>(`${appKey}/setJobId`);
export const setMobileScrollOffsetForList = createAction<number>(`${appKey}/setMobileScrollOffsetForList`);
export const resetCountApplied = createAction<number>(`${appKey}/resetCountApplied`);
export const setJustViewedChats = createAction<any>(`${appKey}/setJustViewedChats`);
export const vetApplicantBatchResultFinished = createAction<any>(`${appKey}/vetApplicantBatchResultFinished`);
export const setShowSaveHoursWithSaudModal = createAction<boolean>(`${appKey}/setShowSaveHoursWithSaudModal`);
export const setShowSaudOnItModal = createAction<boolean>(`${appKey}/setShowSaudOnItModal`);
export const setSaudInterviewsTourStep = createAction<"pick" | "act" | "menu" | "sent" | "summary" | "full" | null>(`${appKey}/setSaudInterviewsTourStep`);
export const forceSetApplicantsList = createAction<ApplicantListItem[]>(`${appKey}/forceSetApplicantsList`);
export const setShowIrrelevantApplicants = createAction<boolean>(`${appKey}/setShowIrrelevantApplicants`);
export const setShowBulkMessageModal = createAction<boolean>(`${appKey}/setShowBulkMessageModal`);
export const setShowShareJobModal = createAction<boolean>(`${appKey}/setShowShareJobModal`);
export const setIsSideBarOpen = createAction<boolean>(`${appKey}/setIsSideBarOpen`);
export const setResetSelectedApplicantsIds = createAction<boolean>(`${appKey}/setResetSelectedApplicantsIds`);
export const setEvaluationGateBlock = createAction<{ applicationExternalIds: string[]; message?: string } | null>(`${appKey}/setEvaluationGateBlock`);
export const setReloadKanbanColumns = createAction<boolean>(`${appKey}/setReloadKanbanColumns`);
export const massHiringFiltersInModal = [LocalFilterEnum.EXPERIENCE, LocalFilterEnum.EDUCATION_LEVEL, LocalFilterEnum.ENGLISH_PROFICIENCY, LocalFilterEnum.OTHER_FILTERS];

export const setApplicantsAppliedKanbanListPage = createAction<number>(`${appKey}/setApplicantsAppliedKanbanListPage`);
export const setApplicantsInterviewedKanbanListPage = createAction<number>(`${appKey}/setApplicantsInterviewedKanbanListPage`);
export const setApplicantsShortlistedKanbanListPage = createAction<number>(`${appKey}/setApplicantsShortlistedKanbanListPage`);
export const setApplicantsFinalistKanbanListPage = createAction<number>(`${appKey}/setApplicantsFinalistKanbanListPage`);
export const setApplicantsAppliedKanbanListLoading = createAction<boolean>(`${appKey}/setApplicantsAppliedKanbanListLoading`);
export const setApplicantsInterviewedKanbanListLoading = createAction<boolean>(`${appKey}/setApplicantsInterviewedKanbanListLoading`);
export const setApplicantsShortlistedKanbanListLoading = createAction<boolean>(`${appKey}/setApplicantsShortlistedKanbanListLoading`);
export const setApplicantsFinalistKanbanListLoading = createAction<boolean>(`${appKey}/setApplicantsFinalistKanbanListLoading`);
export const setApplicantsAppliedKanbanListCount = createAction<number>(`${appKey}/setApplicantsAppliedKanbanListCount`);
export const setApplicantsInterviewedKanbanListCount = createAction<number>(`${appKey}/setApplicantsInterviewedKanbanListCount`);
export const setApplicantsShortlistedKanbanListCount = createAction<number>(`${appKey}/setApplicantsShortlistedKanbanListCount`);
export const setApplicantsFinalistKanbanListCount = createAction<number>(`${appKey}/setApplicantsFinalistKanbanListCount`);
export const setApplicantsAppliedKanbanList = createAction<ApplicantListItem[]>(`${appKey}/setApplicantsAppliedKanbanList`);
export const setApplicantsInterviewedKanbanList = createAction<ApplicantListItem[]>(`${appKey}/setApplicantsInterviewedKanbanList`);
export const setApplicantsShortlistedKanbanList = createAction<ApplicantListItem[]>(`${appKey}/setApplicantsShortlistedKanbanList`);
export const setApplicantsFinalistKanbanList = createAction<ApplicantListItem[]>(`${appKey}/setApplicantsFinalistKanbanList`);
export const setApplicantsToMoveBetweenKanbanColumns = createAction<ApplicantsToMoveBetweenKanbanColumns | null>(`${appKey}/setApplicantsToMoveBetweenKanbanColumns`);
export const resetApplicantsList = createAction<any>(`${appKey}/resetApplicantsList`);
export const setSelectedApplicantNote = createAction<ApplicantNoteItem | null>(`${appKey}/setSelectedApplicantNote`);

// Dynamic columns actions
export const setKanbanColumnPage = createAction<{ columnId: string; page: number }>(`${appKey}/setKanbanColumnPage`);
export const setKanbanColumnLoading = createAction<{ columnId: string; loading: boolean }>(`${appKey}/setKanbanColumnLoading`);
export const setKanbanColumnCount = createAction<{ columnId: string; count: number }>(`${appKey}/setKanbanColumnCount`);
export const setKanbanColumnApplicants = createAction<{ columnId: string; applicants: ApplicantListItem[] }>(`${appKey}/setKanbanColumnApplicants`);
export const resetKanbanColumnsData = createAction<void>(`${appKey}/resetKanbanColumnsData`);
/** Updates applicant(s) scheduledInterviews in the kanban column where each applicant exists (used after send/cancel interview invitation in card view). */
export const updateApplicantsScheduledInterviewsInKanbanColumns = createAction<ApplicantListItem[]>(`${appKey}/updateApplicantsScheduledInterviewsInKanbanColumns`);

export const getDefaultVettingFilters = (job: FullTimeJobDetails, isMassHiring?: boolean): ApplicantsFilter => {
    return {
        skip: 0,
        limit: isMassHiring ? MASS_HIRING_APPLICANTS_PAGE_LIMIT : VETTING_APPLICANTS_PAGE_LIMIT,
        sortBy: ApplicantsSortByEnum.BEST_MATCHED,
        unifiedList: true,
        searchString: '',
        page: 1,
        statuses: isMassHiring ? undefined : [ApplicationStatusEnum.PENDING],
        // genders: isMassHiring ? undefined : job?.gender === GenderEnum.MALE ? [GenderEnum.MALE] : job?.gender === GenderEnum.FEMALE ? [GenderEnum.FEMALE] : undefined
    }
}

export const getFiltersForVettingTab = (filters: ApplicantsFilter, selectedTab: VettingTabsEnum): ApplicantsFilter => {
    const isSuggestedList = selectedTab === VettingTabsEnum.SUGGESTED;
    const isVettedList = selectedTab === VettingTabsEnum.VETTED;
    let selectedStatus = ApplicationStatusEnum.PENDING;

    if (selectedTab === VettingTabsEnum.SHORTLIST) {
        selectedStatus = ApplicationStatusEnum.SHORT_LISTED;
    } else if (selectedTab === VettingTabsEnum.FINALIST) {
        selectedStatus = ApplicationStatusEnum.FINALIST;
    } else if (selectedTab === VettingTabsEnum.REJECTED) {
        selectedStatus = ApplicationStatusEnum.NOT_SELECTED;
    }

    const returnRecommendations = isSuggestedList ? true : selectedTab === VettingTabsEnum.APPLIED ? false : undefined;

    return {
        ...filters,
        skip: 0,
        page: 1,
        statuses: isVettedList ? undefined : [selectedStatus],
        vettedBySaud: isVettedList ? true : undefined,
        returnRecommendations,
        ...(returnRecommendations === false ? { unifiedList: false } : {}),
    };
}

/**
 * Default list tab when opening a job:
 * - Applicants present (with or without suggestions) → Applied
 * - Suggestions only (or still generating) → Suggested
 * - Neither → Applied empty state (avoid focusing on missing recommendations)
 * - Post create/edit may pass Suggested via fromState while recommendations generate
 */
export const resolveDefaultVettingTab = (
    job: { recommendationsCount?: number; applicantsCount?: number; isRecommending?: boolean } | undefined,
    fromState: VettingTabsEnum | undefined,
    isEnterprise: boolean,
): VettingTabsEnum => {
    if (isEnterprise) return VettingTabsEnum.APPLIED;

    // After create/edit, stay on Suggested while recommendations are generating
    if (fromState === VettingTabsEnum.SUGGESTED && job?.isRecommending) {
        return VettingTabsEnum.SUGGESTED;
    }

    const hasApplicants = (job?.applicantsCount || 0) > 0;
    if (hasApplicants) return VettingTabsEnum.APPLIED;

    const hasSuggestions = (job?.recommendationsCount || 0) > 0;
    if (hasSuggestions || job?.isRecommending) return VettingTabsEnum.SUGGESTED;

    return VettingTabsEnum.APPLIED;
}

export const getDefaultSelectedFilters = (job: FullTimeJobDetails): ApplicantsFiltersEnumObject => {
    const filter: any = {};
    // if (job?.gender === GenderEnum.MALE) {
    //     filter.gender = { 'MALE': true };
    // }
    // if (job?.gender === GenderEnum.FEMALE) {
    //     filter.gender = { 'FEMALE': true };
    // }
    return filter;
}

export const getDefaultOpenSections = () => {
    return {
        age: false,
        applicants: true,
        districts: false,
        education_level: false,
        english_proficiency: false,
        experience_by_industry: false,
        gender: false,
        nationality: false,
        preferred_contract_type: false,
        salary_expectation: false,
        years_of_experience: false
    }
}

const getInvalidationParams = (state: VettingStoreInferface) => {
    let invalidationObject = { ...state.filters };
    delete invalidationObject.page;
    if (!invalidationObject.searchString) delete invalidationObject.searchString;
    return invalidationObject
}

export const getInvalidationParamsFilters = (filters: ApplicantsFilter) => {
    let invalidationObject = { ...filters };
    delete invalidationObject.page;
    if (!invalidationObject.searchString) delete invalidationObject.searchString;
    return invalidationObject
}

export const getListApplicantsApiUrl = (jobId: string) => {
    return `${apiModuleUrl}/${jobId}/list`;
}

const syncApplicantInKanban = (state: VettingStoreInferface, applicant: ApplicantListItem) => {
    const id = applicant?.externalId;
    if (!id || !state.kanbanColumnsData) return;

    Object.keys(state.kanbanColumnsData).forEach((columnId) => {
        const column = state.kanbanColumnsData[columnId];
        if (!column?.applicants?.some((item) => item.externalId === id)) return;
        column.applicants = updateObjectInList(column.applicants, id, applicant);
    });
}

const initialState: VettingStoreInferface = {
    jobId: null,
    isLoadingList: true,
    isLoadingUnlocking: false,
    isLoadingSingleAction: false,
    loadingSingleActionApplicantId: null,
    isLoadingMultipleAction: false,
    isLoadingTalentAcquisitionSync: false,
    isLoadingApplicantDetails: false,
    isLoadingRateInterview: false,
    selectedApplicant: null,
    activeTab: VettingTabsEnum.APPLIED,
    activeDetailsTab: VettingDetailsTabsEnum.PROFILE,
    applicantsList: [],
    totalCount: null,
    totalCountOfSelectedTab: 0,
    countSuggested: 0,
    countApplied: 0,
    countVetted: 0,
    countShortlist: 0,
    countFinalist: 0,
    countRejected: 0,
    filters: getDefaultVettingFilters(null),
    selectedFilters: {},
    selectedFiltersRow: {},
    isCurrentPageResetFromFilters: false,
    isFiltersMenuVisible: false,
    isFiltersReset: false,
    lookupsData: null,
    viewsRemaining: null,
    recentlyViewedApplicants: [],
    selectedApplicants: [],
    filterErrors: {},
    showSubscriptionModal: false,
    showCloseJobModal: false,
    showReopenJobModal: false,
    showConsumedJobPostModal: false,
    showUnlocksConsumedModal: false,
    showRecommendationEducationalModal: false,
    showInterviewMethodModal: false,
    isBackendRecommending: false,
    mobileScrollOffsetForList: null,
    justViewedChats: [],
    showCancelInterviewsModal: false,
    interviewDetails: null,
    isLoadingInterviewDetails: false,
    isLoadingFetchingVideoInterview: false,
    showSaveHoursWithSaudModal: false,
    showSaudOnItModal: false,
    saudInterviewsTourStep: null,
    showIrrelevantApplicants: false,
    showAiMatchingLabels: false,
    showBulkMessageModal: false,
    loadingSendBulkMsg: false,
    showShareJobModal: false,
    isSideBarOpen: false,
    resetSelectedApplicantsIds: false,
    evaluationGateBlock: null,

    applicantsAppliedKanbanList: [],
    applicantsAppliedKanbanListCount: 0,
    applicantsInterviewedKanbanList: [],
    applicantsInterviewedKanbanListCount: 0,
    applicantsShortlistedKanbanList: [],
    applicantsShortlistedKanbanListCount: 0,
    applicantsFinalistKanbanList: [],
    applicantsFinalistKanbanListCount: 0,
    applicantsAppliedKanbanListPage: 0,
    applicantsInterviewedKanbanListPage: 0,
    applicantsShortlistedKanbanListPage: 0,
    applicantsFinalistKanbanListPage: 0,
    applicantsAppliedKanbanListLoading: false,
    applicantsInterviewedKanbanListLoading: false,
    applicantsShortlistedKanbanListLoading: false,
    applicantsFinalistKanbanListLoading: false,
    kanbanColumnsCounts: {}, // Dynamic column counts: columnId -> count
    // Dynamic columns
    kanbanColumns: [],
    kanbanColumnsData: {},
    isLoadingKanbanColumns: false,
    reloadKanbanColumns: false,
    applicantsToMoveBetweenKanbanColumns: null,

    // Dynamic application statuses
    applicationStatuses: [],
    isLoadingApplicationStatuses: false,
    applicantNotes: [],
    selectedApplicantNote: null,
    isLoadingApplicantNotes: false,
    isSavingApplicantNote: false,
    jobDynamicStages: [],
    isLoadingJobDynamicStages: false,
    isLoadingDeleteJobDynamicStages: false,
    isLoadingUpsertJobDynamicStages: false,
}

export const appApplicantsSlice = createSlice({
    name: appKey,
    initialState,
    reducers: {},
    extraReducers: builder => {
        builder
            .addCase(setIsLoadingList, (state, action) => {
                state.isLoadingList = action.payload;
            })
            .addCase(getVettingApplicantsList.pending, (state, action) => {
                if (action.meta.arg.backgroundRefresh) return;
                state.isLoadingList = true;
            })
            .addCase(getVettingApplicantsList.fulfilled, (state, action) => {
                const resData: ApplicantsListResponse = action.payload;
                if (action.meta.arg.backgroundRefresh) {
                    if (resData?.applicants) {
                        state.applicantsList = resData.applicants;
                        const selectedId = state.selectedApplicant?.externalId;
                        const fresh = selectedId ? resData.applicants.find((item) => item.externalId === selectedId) : undefined;
                        if (fresh) {
                            state.selectedApplicant = fresh;
                            state.interviewDetails = null;
                            syncApplicantInKanban(state, fresh);
                        }
                    }
                    return;
                }
                state.isLoadingList = false;
                state.applicantsList = resData?.applicants;
                state.lookupsData = resData?.lookupsData;
                state.viewsRemaining = resData?.viewsRemaining;
                state.totalCount = resData?.total;
                state.showAiMatchingLabels = resData?.showAiMatchingLabels;
                const pendingCount = resData?.total - (resData?.shortListedCount + resData?.notSelectedCount);

                if (!resData?.fromCache || (state.countSuggested + state.countApplied + state.countVetted + state.countShortlist + state.countRejected === 0)) {
                    state.countSuggested = resData.recommendedCount || 0;
                    state.countApplied = resData.pendingCount;
                    state.countVetted = resData?.vettedBySaudCount;
                    state.countShortlist = resData?.shortListedCount;
                    state.countRejected = resData?.notSelectedCount;
                    state.countFinalist = resData?.finalistCount;
                }

                if (resData?.fromCache && resData?.pendingCount && !state.countApplied) {
                    state.countApplied = resData.pendingCount;
                }

                if (resData?.fromCache && resData?.recommendedCount) {
                    state.countSuggested = resData.recommendedCount;
                }

                state.totalCountOfSelectedTab = pendingCount;

                if (state.filters?.returnRecommendations) {
                    state.totalCountOfSelectedTab = resData?.recommendedCount ?? resData?.total ?? 0;
                } else if (state.isBackendRecommending || (state.filters?.statuses?.includes(ApplicationStatusEnum.PENDING) && !state.filters?.vettedBySaud && (resData?.pendingCount === 0 || (resData?.shortListedCount === 0 && resData?.notSelectedCount === 0)))) {
                    state.totalCountOfSelectedTab = pendingCount; // this is the case for recommendations  
                } else if (state.filters?.statuses?.includes(ApplicationStatusEnum.PENDING) && !state.filters?.vettedBySaud) {
                    state.totalCountOfSelectedTab = pendingCount;
                } else if (state.filters?.vettedBySaud) {
                    state.totalCountOfSelectedTab = resData?.vettedBySaudCount;
                } else if (state.filters?.statuses?.includes(ApplicationStatusEnum.SHORT_LISTED)) {
                    state.totalCountOfSelectedTab = resData?.shortListedCount;
                } else if (state.filters?.statuses?.includes(ApplicationStatusEnum.NOT_SELECTED)) {
                    state.totalCountOfSelectedTab = resData?.notSelectedCount;
                } else if (state.filters?.statuses?.includes(ApplicationStatusEnum.FINALIST)) {
                    state.totalCountOfSelectedTab = resData?.finalistCount || 0;
                }

                state.isBackendRecommending = false;
            })
            .addCase(resetApplicantsList, (state, action) => {
                state.applicantsList = [];
                state.totalCount = 0;
                state.countSuggested = 0;
                state.countApplied = 0;
                state.countVetted = 0;
                state.countShortlist = 0;
                state.countRejected = 0;
                state.countFinalist = 0;
                state.totalCountOfSelectedTab = 0;
            })
            .addCase(getVettingApplicantsList.rejected, (state, action) => {
                if (action.meta.arg.backgroundRefresh) return;
                state.isLoadingList = false;
                let errorMsg = action.error?.message;
                if (errorMsg === 'Rejected') {
                    errorMsg = t(`error_fetching_applicants`);
                }
                // Toast.Error({ content: errorMsg });
                const errorString = JSON.stringify({ report: `Error in fetching applicants`, error: action.error?.message, message: errorMsg, partnerId: localStorage.getItem(FULLTIME_PARTNER_ID_KEY) });
                captureMessage(errorString);
            })
            .addCase(forceSetApplicantsList, (state, action) => {
                state.applicantsList = action.payload;
            })
            .addCase(setActiveVettingTab, (state, action) => {
                state.activeTab = action.payload;
            })
            .addCase(setActiveVettingDetailsTab, (state, action) => {
                state.activeDetailsTab = action.payload;
            })
            .addCase(setJobId, (state, action) => {
                if (state.jobId !== action.payload) {
                    state.countSuggested = 0;
                    state.countApplied = 0;
                    state.countVetted = 0;
                    state.countShortlist = 0;
                    state.countRejected = 0;
                    state.countFinalist = 0;
                    state.applicantsList = [];
                    state.totalCount = 0;
                    state.totalCountOfSelectedTab = 0;
                }
                state.jobId = action.payload;
            })
            .addCase(updateSelectedApplicant, (state, action) => {
                state.selectedApplicant = action.payload;
                state.interviewDetails = null;
            })
            .addCase(setShowBulkMessageModal, (state, action) => {
                state.showBulkMessageModal = action.payload;
            })
            .addCase(setShowShareJobModal, (state, action) => {
                state.showShareJobModal = action.payload;
            })
            .addCase(setShowIrrelevantApplicants, (state, action) => {
                state.showIrrelevantApplicants = action.payload;
            })
            .addCase(getApplicantDetails.pending, (state, action) => {
                state.isLoadingApplicantDetails = true;
            })
            .addCase(getApplicantDetails.fulfilled, (state, action) => {
                const applicant: ApplicantListItem = action.payload;
                const skipViewsDecrement = action.meta.arg.skipViewsDecrement;
                state.isLoadingApplicantDetails = false;
                if (!skipViewsDecrement) {
                    state.viewsRemaining = state.viewsRemaining - 1;
                }
                const stillOnThisApplicant = !state.selectedApplicant || state.selectedApplicant.externalId === applicant.externalId;
                if (stillOnThisApplicant) {
                    state.selectedApplicant = applicant;
                    if (skipViewsDecrement) {
                        state.interviewDetails = null;
                    }
                }
                state.applicantsList = updateObjectInList(state.applicantsList, applicant.externalId, applicant);
                if (skipViewsDecrement) {
                    syncApplicantInKanban(state, applicant);
                }
                if (typeof applicant.showAiMatchingLabels === 'boolean') {
                    state.showAiMatchingLabels = applicant.showAiMatchingLabels;
                }
                invalidateJobPostCache(state.jobId);
            })
            .addCase(getApplicantDetails.rejected, (state, action) => {
                state.isLoadingApplicantDetails = false;
            })
            .addCase(getJobDetails.fulfilled, (state, action) => {
                if (action.payload && typeof action.payload.showAiMatchingLabels === 'boolean') {
                    state.showAiMatchingLabels = action.payload.showAiMatchingLabels;
                }
            })
            .addCase(setIsFiltersMenuVisible, (state, action) => {
                state.isFiltersMenuVisible = action.payload;
            })
            .addCase(setIsSideBarOpen, (state, action) => {
                state.isSideBarOpen = action.payload;
            })
            .addCase(setMobileScrollOffsetForList, (state, action) => {
                state.mobileScrollOffsetForList = action.payload;
            })
            .addCase(setFilters, (state, action) => {
                state.filters = action.payload;
            })
            .addCase(setSelectedFilters, (state, action) => {
                state.selectedFilters = action.payload;
            })
            .addCase(setSelectedFiltersRow, (state, action) => {
                state.selectedFiltersRow = action.payload;
            })
            .addCase(setShowSaveHoursWithSaudModal, (state, action) => {
                state.showSaveHoursWithSaudModal = action.payload;
            })
            .addCase(setShowSaudOnItModal, (state, action) => {
                state.showSaudOnItModal = action.payload;
            })
            .addCase(setSaudInterviewsTourStep, (state, action) => {
                state.saudInterviewsTourStep = action.payload;
            })
            .addCase(setEvaluationGateBlock, (state, action) => {
                state.evaluationGateBlock = action.payload;
            })
            .addCase(setSelectedApplicants, (state, action) => {
                state.selectedApplicants = action.payload;
            })
            .addCase(updateSingleApplicantStatus.pending, (state, action) => {
                state.isLoadingSingleAction = true;
                state.loadingSingleActionApplicantId = action.meta.arg.offlineJobApplicationIds?.[0] || action.meta.arg.jobApplicationIds?.[0];
                if (action.meta.arg?.status === ApplicationStatusEnum.NOT_SELECTED && action.meta.arg?.isKanbanView) {
                    state.applicantsAppliedKanbanList = state.applicantsAppliedKanbanList.filter(l => l.externalId !== action.meta.arg.kanbanApplicant?.externalId);
                    state.applicantsShortlistedKanbanList = state.applicantsShortlistedKanbanList.filter(l => l.externalId !== action.meta.arg.kanbanApplicant?.externalId);
                    state.applicantsInterviewedKanbanList = state.applicantsInterviewedKanbanList.filter(l => l.externalId !== action.meta.arg.kanbanApplicant?.externalId);
                    state.applicantsFinalistKanbanList = state.applicantsFinalistKanbanList.filter(l => l.externalId !== action.meta.arg.kanbanApplicant?.externalId);
                }
            })
            .addCase(updateSingleApplicantStatus.fulfilled, (state, action) => {
                state.isLoadingSingleAction = false;
                const isInterviewed = action?.payload?.isInterviewed;
                if (isInterviewed) {
                    Toast.Success({ content: t(`applicant_updated_interviewed`) });
                } else {
                    Toast.Success({ content: t(`applicant_updated_${action?.payload?.status || ''}`) });
                }

                const selectedApplicant = state.selectedApplicant || action?.payload?.kanbanApplicant as ApplicantListItem;
                const newStatus = action?.payload?.status as ApplicationStatusEnum;
                const originalStatus = selectedApplicant?.status as ApplicationStatusEnum || action?.payload?.originalStatus as ApplicationStatusEnum;
                const isMassHiring = action?.payload?.isMassHiring;
                const isKanbanView = action?.payload?.isKanbanView;
                const isRejectedTabActive = action?.payload?.isRejectedTabActive;
                const sourceColumnId = action?.meta?.arg?.sourceColumnId;
                const destinationColumnId = action?.meta?.arg?.destinationColumnId;
                const needToFilterList = !isMassHiring || (isMassHiring && newStatus === ApplicationStatusEnum.NOT_SELECTED) || (isMassHiring && newStatus === ApplicationStatusEnum.PENDING && isRejectedTabActive);

                // Get stage name from applicationStatuses if newStageExternalId is provided
                const newStageExternalId = action?.payload?.newStageExternalId;
                let newStageName: string | undefined;
                if (newStageExternalId && state.applicationStatuses) {
                    const stageDef = state.applicationStatuses.find(s => s.value === newStageExternalId);
                    newStageName = stageDef?.label;
                }

                if (newStatus === ApplicationStatusEnum.PENDING) {
                    state.countApplied = originalStatus !== ApplicationStatusEnum.PENDING ? state.countApplied + 1 : state.countApplied;
                    state.countShortlist = originalStatus === ApplicationStatusEnum.SHORT_LISTED ? state.countShortlist - 1 : state.countShortlist;
                    state.countRejected = originalStatus === ApplicationStatusEnum.NOT_SELECTED ? state.countRejected - 1 : state.countRejected;
                    state.countFinalist = originalStatus === ApplicationStatusEnum.FINALIST ? state.countFinalist - 1 : state.countFinalist;
                }

                if (newStatus === ApplicationStatusEnum.SHORT_LISTED) {
                    state.countApplied = originalStatus === ApplicationStatusEnum.PENDING ? state.countApplied - 1 : state.countApplied;
                    state.countShortlist = originalStatus !== ApplicationStatusEnum.SHORT_LISTED ? state.countShortlist + 1 : state.countShortlist;
                    state.countRejected = originalStatus === ApplicationStatusEnum.NOT_SELECTED ? state.countRejected - 1 : state.countRejected;
                    state.countFinalist = originalStatus === ApplicationStatusEnum.FINALIST ? state.countFinalist - 1 : state.countFinalist;
                }

                if (newStatus === ApplicationStatusEnum.NOT_SELECTED) {
                    state.countApplied = originalStatus === ApplicationStatusEnum.PENDING ? state.countApplied - 1 : state.countApplied;
                    state.countShortlist = originalStatus === ApplicationStatusEnum.SHORT_LISTED ? state.countShortlist - 1 : state.countShortlist;
                    state.countRejected = originalStatus !== ApplicationStatusEnum.NOT_SELECTED ? state.countRejected + 1 : state.countRejected;
                    state.countFinalist = originalStatus === ApplicationStatusEnum.FINALIST ? state.countFinalist - 1 : state.countFinalist;
                }

                if (newStatus === ApplicationStatusEnum.FINALIST) {
                    state.countApplied = originalStatus === ApplicationStatusEnum.PENDING ? state.countApplied - 1 : state.countApplied;
                    state.countShortlist = originalStatus === ApplicationStatusEnum.SHORT_LISTED ? state.countShortlist - 1 : state.countShortlist;
                    state.countRejected = originalStatus === ApplicationStatusEnum.NOT_SELECTED ? state.countRejected - 1 : state.countRejected;
                    state.countFinalist = originalStatus !== ApplicationStatusEnum.FINALIST ? state.countFinalist + 1 : state.countFinalist;
                }

                // state.kanbanColumnsCounts = {
                //     applied: state.kanbanColumnsCounts?.applied - state.kanbanColumnsCounts?.interviewed,
                //     interviewed: state.kanbanColumnsCounts?.interviewed,
                //     shortlisted: state.kanbanColumnsCounts?.shortlisted + 1,
                //     finalist: state.kanbanColumnsCounts?.finalist,
                // }

                // kanbanColumnsCounts is now dynamic and updated via getVettingApplicantsListForKanbanView
                // No longer updating static fields here

                // Update dynamic kanban column counts and move applicant when moving between columns
                if (isKanbanView && sourceColumnId && destinationColumnId && sourceColumnId !== destinationColumnId) {
                    // For kanban view, get applicant from meta.arg (original request) or payload
                    // The kanbanApplicant is passed in the request, not returned in the response
                    const applicant = (action?.meta?.arg?.kanbanApplicant as ApplicantListItem) || (action?.payload?.kanbanApplicant as ApplicantListItem) || selectedApplicant;

                    if (applicant && applicant.externalId) {
                        // Remove applicant from source column
                        if (state.kanbanColumnsData[sourceColumnId]) {
                            state.kanbanColumnsData[sourceColumnId].applicants = state.kanbanColumnsData[sourceColumnId].applicants.filter(
                                (a) => a.externalId !== applicant.externalId
                            );
                            // Decrement source column count
                            // Get current count from either kanbanColumnsCounts or kanbanColumnsData.count
                            // Prioritize kanbanColumnsCounts, but fallback to kanbanColumnsData.count if not set
                            let currentCount: number;
                            if (state.kanbanColumnsCounts[sourceColumnId] !== undefined && state.kanbanColumnsCounts[sourceColumnId] !== null) {
                                currentCount = state.kanbanColumnsCounts[sourceColumnId];
                            } else if (state.kanbanColumnsData[sourceColumnId]?.count !== undefined && state.kanbanColumnsData[sourceColumnId]?.count !== null) {
                                currentCount = state.kanbanColumnsData[sourceColumnId].count;
                                // Sync kanbanColumnsCounts with kanbanColumnsData.count if it wasn't set
                                state.kanbanColumnsCounts[sourceColumnId] = currentCount;
                            } else {
                                currentCount = 0;
                                // Initialize both if neither exists
                                state.kanbanColumnsCounts[sourceColumnId] = 0;
                            }

                            // Always decrement (Math.max ensures we don't go below 0)
                            const newCount = Math.max(0, currentCount - 1);
                            state.kanbanColumnsCounts[sourceColumnId] = newCount;
                            state.kanbanColumnsData[sourceColumnId].count = newCount;
                        }

                        // Add applicant to destination column
                        if (state.kanbanColumnsData[destinationColumnId]) {
                            // Check if applicant already exists in destination (avoid duplicates)
                            const existsInDestination = state.kanbanColumnsData[destinationColumnId].applicants.some(
                                (a) => a.externalId === applicant.externalId
                            );
                            if (!existsInDestination) {
                                // Update applicant status and stage info before adding
                                const updatedApplicant = {
                                    ...applicant,
                                    status: newStatus,
                                    ...(action?.payload?.isInterviewed && { invitedForVetting: true }),
                                    stageExternalId: newStageExternalId,
                                    stageName: newStageName,
                                };
                                state.kanbanColumnsData[destinationColumnId].applicants = [
                                    ...state.kanbanColumnsData[destinationColumnId].applicants,
                                    updatedApplicant
                                ];
                                // Increment destination column count
                                if (state.kanbanColumnsCounts[destinationColumnId] !== undefined) {
                                    state.kanbanColumnsCounts[destinationColumnId] = (state.kanbanColumnsCounts[destinationColumnId] || 0) + 1;
                                    state.kanbanColumnsData[destinationColumnId].count = state.kanbanColumnsCounts[destinationColumnId];
                                }
                            }
                        }
                    }
                }

                if (selectedApplicant) {
                    const updateData: Partial<ApplicantListItem> = {
                        status: action?.payload?.status,
                        stageExternalId: newStageExternalId,
                        stageName: newStageName,
                    };

                    state.selectedApplicant = {
                        ...selectedApplicant,
                        ...updateData
                    };

                    if (needToFilterList) {
                        state.applicantsList = [...state.applicantsList?.filter(l => l.externalId !== selectedApplicant.externalId)];
                    } else {
                        state.applicantsList = updateObjectInList(state.applicantsList, selectedApplicant.externalId, updateData);
                        // TODO fix if kanban view, update the status in the applicants list of kanban columns
                    }


                } else if (isKanbanView) {
                    // if (newStatus === ApplicationStatusEnum.PENDING) {
                    //     state.applicantsAppliedKanbanList = state.applicantsAppliedKanbanList.filter(l => l.externalId !== selectedApplicant.externalId);
                    // } else if (newStatus === ApplicationStatusEnum.SHORT_LISTED) {
                    //     state.applicantsShortlistedKanbanList = state.applicantsShortlistedKanbanList.filter(l => l.externalId !== selectedApplicant.externalId);
                    // } else if (newStatus === ApplicationStatusEnum.FINALIST) {
                    //     state.applicantsAppliedKanbanList = state.applicantsAppliedKanbanList.filter(l => l.externalId !== selectedApplicant.externalId);
                    //     state.applicantsShortlistedKanbanList = state.applicantsShortlistedKanbanList.filter(l => l.externalId !== selectedApplicant.externalId);
                    //     state.applicantsInterviewedKanbanList = state.applicantsInterviewedKanbanList.filter(l => l.externalId !== selectedApplicant.externalId);
                    //     state.applicantsFinalistKanbanList = [...state.applicantsFinalistKanbanList, selectedApplicant];
                    // }

                } else if (state.loadingSingleActionApplicantId) {
                    const updateData: Partial<ApplicantListItem> = {
                        status: newStatus,
                        stageExternalId: newStageExternalId,
                        stageName: newStageName,
                    };

                    if (needToFilterList) {
                        state.applicantsList = [...state.applicantsList?.filter(l => l.externalId !== state.loadingSingleActionApplicantId)];
                    } else {
                        state.applicantsList = updateObjectInList(state.applicantsList, state.loadingSingleActionApplicantId, updateData);
                    }

                }
                state.loadingSingleActionApplicantId = null;
                invalidateJobPostCache(state.jobId);

            })
            .addCase(updateSingleApplicantStatus.rejected, (state, action: any) => {
                state.isLoadingSingleAction = false;
                state.loadingSingleActionApplicantId = null;
                if (action.payload?.code === 'FT-100') {
                    state.evaluationGateBlock = {
                        applicationExternalIds: [
                            ...(action.payload.jobApplicationIds || []),
                            ...(action.payload.offlineJobApplicationIds || []),
                        ],
                        message: action.payload.message,
                    };
                }
                Toast.Error({ content: action.payload?.message || action.error?.message });
            })

            .addCase(updateMultipleApplicantsStatus.pending, (state, action) => {
                state.isLoadingMultipleAction = true;
            })
            .addCase(updateMultipleApplicantsStatus.fulfilled, (state, action) => {
                state.isLoadingMultipleAction = false;
                Toast.Success({ content: t(`applicants_updated_${action?.payload?.status || ''}`) });

                const selectedApplicantsIds = state.selectedApplicants.map(a => a.externalId);
                const countSelectedApplicantsIds = selectedApplicantsIds.length;
                const newStatus = action?.payload?.status as ApplicationStatusEnum;
                const originalStatus = action?.payload?.originalStatus as ApplicationStatusEnum;
                const isMassHiring = action?.payload?.isMassHiring;
                const needToFilterList = !isMassHiring || (isMassHiring && (newStatus === ApplicationStatusEnum.NOT_SELECTED || newStatus === ApplicationStatusEnum.PENDING));
                const isKanbanView = action?.payload?.isKanbanView;

                if (needToFilterList) {
                    state.applicantsList = state.applicantsList.filter(({ externalId }) => !selectedApplicantsIds.includes(externalId));
                }


                if (isKanbanView) {
                    // kanbanColumnsCounts is now dynamic and updated via getVettingApplicantsListForKanbanView
                    // Counts are managed per column using the total from API responses
                    // No longer updating static fields here

                    if (newStatus === ApplicationStatusEnum.NOT_SELECTED) {
                        state.countRejected = state.countRejected + countSelectedApplicantsIds;
                    }
                } else {
                    if (newStatus === ApplicationStatusEnum.PENDING) {
                        state.countApplied = originalStatus !== ApplicationStatusEnum.PENDING ? state.countApplied + countSelectedApplicantsIds : state.countApplied;
                        state.countShortlist = originalStatus === ApplicationStatusEnum.SHORT_LISTED ? state.countShortlist - countSelectedApplicantsIds : state.countShortlist;
                        state.countRejected = originalStatus === ApplicationStatusEnum.NOT_SELECTED ? state.countRejected - countSelectedApplicantsIds : state.countRejected;
                        state.countFinalist = originalStatus === ApplicationStatusEnum.FINALIST ? state.countFinalist - countSelectedApplicantsIds : state.countFinalist;
                    }

                    if (newStatus === ApplicationStatusEnum.SHORT_LISTED) {
                        state.countApplied = originalStatus === ApplicationStatusEnum.PENDING ? state.countApplied - countSelectedApplicantsIds : state.countApplied;
                        state.countShortlist = originalStatus !== ApplicationStatusEnum.SHORT_LISTED ? state.countShortlist + countSelectedApplicantsIds : state.countShortlist;
                        state.countRejected = originalStatus === ApplicationStatusEnum.NOT_SELECTED ? state.countRejected - countSelectedApplicantsIds : state.countRejected;
                        state.countFinalist = originalStatus === ApplicationStatusEnum.FINALIST ? state.countFinalist - countSelectedApplicantsIds : state.countFinalist;
                    }

                    if (newStatus === ApplicationStatusEnum.NOT_SELECTED) {
                        state.countApplied = originalStatus === ApplicationStatusEnum.PENDING ? state.countApplied - countSelectedApplicantsIds : state.countApplied;
                        state.countShortlist = originalStatus === ApplicationStatusEnum.SHORT_LISTED ? state.countShortlist - countSelectedApplicantsIds : state.countShortlist;
                        state.countRejected = originalStatus !== ApplicationStatusEnum.NOT_SELECTED ? state.countRejected + countSelectedApplicantsIds : state.countRejected;
                        state.countFinalist = originalStatus === ApplicationStatusEnum.FINALIST ? state.countFinalist - countSelectedApplicantsIds : state.countFinalist;
                    }

                    if (newStatus === ApplicationStatusEnum.FINALIST) {
                        state.countApplied = originalStatus === ApplicationStatusEnum.PENDING ? state.countApplied - countSelectedApplicantsIds : state.countApplied;
                        state.countShortlist = originalStatus === ApplicationStatusEnum.SHORT_LISTED ? state.countShortlist - countSelectedApplicantsIds : state.countShortlist;
                        state.countRejected = originalStatus === ApplicationStatusEnum.NOT_SELECTED ? state.countRejected - countSelectedApplicantsIds : state.countRejected;
                        state.countFinalist = originalStatus !== ApplicationStatusEnum.FINALIST ? state.countFinalist + countSelectedApplicantsIds : state.countFinalist;
                    }
                }


                // Get stage name from applicationStatuses if newStageExternalId is provided
                const newStageExternalId = action?.payload?.newStageExternalId;
                let newStageName: string | undefined;
                if (newStageExternalId && state.applicationStatuses) {
                    const stageDef = state.applicationStatuses.find(s => s.value === newStageExternalId);
                    newStageName = stageDef?.label;
                }

                if (!needToFilterList) {
                    const updateData: Partial<ApplicantListItem> = {
                        status: newStatus,
                        stageExternalId: newStageExternalId,
                        stageName: newStageName,
                    };
                    state.applicantsList = updateObjectsInList(state.applicantsList, selectedApplicantsIds, updateData);
                }

                state.selectedApplicants = [];
                invalidateJobPostCache(state.jobId);
            })
            .addCase(updateMultipleApplicantsStatus.rejected, (state, action: any) => {
                state.isLoadingMultipleAction = false;
                if (action.payload?.code === 'FT-100') {
                    state.evaluationGateBlock = {
                        applicationExternalIds: [
                            ...(action.payload.jobApplicationIds || []),
                            ...(action.payload.offlineJobApplicationIds || []),
                        ],
                        message: action.payload.message,
                    };
                }
                Toast.Error({ content: action.payload?.message || action.error?.message });
            })

            .addCase(setShowSubscriptionModal, (state, action) => {
                state.showSubscriptionModal = action.payload;
            })
            .addCase(setShowCloseJobModal, (state, action) => {
                state.showCloseJobModal = action.payload;
            })
            .addCase(setShowReopenJobModal, (state, action) => {
                state.showReopenJobModal = action.payload;
            })
            .addCase(setShowConsumedJobPostModal, (state, action) => {
                state.showConsumedJobPostModal = action.payload;
            })
            .addCase(setShowInterviewMethodModal, (state, action) => {
                state.showInterviewMethodModal = action.payload;
            })
            .addCase(setShowUnlocksConsumedModal, (state, action) => {
                state.showUnlocksConsumedModal = action.payload;
            })
            .addCase(setShowRecommendationEducationalModal, (state, action) => {
                state.showRecommendationEducationalModal = action.payload;
            })
            .addCase(setShowCancelInterviewsModal, (state, action) => {
                state.showCancelInterviewsModal = action.payload;
            })
            .addCase(setIsBackendRecommending, (state, action) => {
                state.isBackendRecommending = action.payload;
            })
            .addCase(updateRecentlyViewedApplicants, (state, action) => {
                state.recentlyViewedApplicants = action.payload;
            })
            .addCase(unlockApplicantVetting.pending, (state, action) => {
                state.isLoadingUnlocking = true;
            })
            .addCase(unlockApplicantVetting.fulfilled, (state, action) => {
                state.isLoadingUnlocking = false;
                const unlockRes = action?.payload;
                Toast.Success({ content: t(`contact_unlocked`) });
                const selectedApplicant = state.selectedApplicant;

                if (selectedApplicant) {
                    state.selectedApplicant = {
                        ...selectedApplicant,
                        isUnlocked: true,
                        ...unlockRes
                    };
                    state.applicantsList = updateObjectInList(state.applicantsList, selectedApplicant.externalId, { ...unlockRes, canViewDetails: true, isUnlocked: true });
                    invalidateJobPostCache(state.jobId);
                }
            })
            .addCase(unlockApplicantVetting.rejected, (state, action) => {
                state.isLoadingUnlocking = false;
                Toast.Error({ content: action.error?.message })
            })
            .addCase(getApplicantDetailsAndUnlock.pending, (state, action) => {
                state.isLoadingApplicantDetails = true;
                state.isLoadingUnlocking = true;
            })
            .addCase(getApplicantDetailsAndUnlock.fulfilled, (state, action) => {
                state.isLoadingUnlocking = false;
                state.isLoadingApplicantDetails = false;
                const unlockRes = action?.payload;
                const selectedApplicant = state.selectedApplicant;

                if (selectedApplicant) {
                    state.selectedApplicant = {
                        ...selectedApplicant,
                        ...unlockRes,
                        isUnlocked: true,
                        canViewDetails: true,
                    };
                    state.applicantsList = updateObjectInList(state.applicantsList, selectedApplicant.externalId, { ...unlockRes, canViewDetails: true, isUnlocked: true });
                    invalidateJobPostCache(state.jobId);
                }
            })
            .addCase(getApplicantDetailsAndUnlock.rejected, (state, action) => {
                state.isLoadingUnlocking = false;
                state.isLoadingApplicantDetails = false;
                Toast.Error({ content: action.error?.message })
            })

            .addCase(setIsFiltersReset, (state, action) => {
                state.isFiltersReset = action.payload;
            })
            .addCase(vetApplicant.pending, (state, action) => {
                state.isLoadingSingleAction = true;
            })
            .addCase(vetApplicant.fulfilled, (state, action) => {
                state.isLoadingSingleAction = false;
                Toast.Success({ content: t('vetting_started') });
                const selectedApplicant = state.selectedApplicant;
                if (selectedApplicant) {
                    state.selectedApplicant = {
                        ...selectedApplicant,
                        invitedForVetting: true,
                        vettingStatus: AiInterviewStatusEnum.PENDING,
                        status: ApplicationStatusEnum.PENDING,
                    };
                    state.applicantsList = updateObjectInList(state.applicantsList, selectedApplicant.externalId, { invitedForVetting: true, vettingStatus: AiInterviewStatusEnum.PENDING, status: ApplicationStatusEnum.PENDING });
                    invalidateJobPostCache(state.jobId);

                    state.countVetted = state.countVetted + 1;
                }
            })
            .addCase(vetApplicant.rejected, (state, action) => {
                state.isLoadingSingleAction = false;
                Toast.Error({ content: action.error?.message });
            })
            .addCase(vetApplicantsBatch.pending, (state, action) => {
            })
            .addCase(vetApplicantsBatch.fulfilled, (state, action) => {
                Toast.Success({ content: t('applicants_invited_for_interview') });
                const jobApplicationIds = action?.payload?.jobApplicationIds ?? [];
                const countSelectedApplicantsIds = jobApplicationIds.length;
                const pendingApplicantsIds = action?.payload?.pendingApplicantsIds ?? [];
                const countPendingApplicantsIds = pendingApplicantsIds.length;
                const kanbanView = action?.payload?.isKanbanView;

                state.applicantsList = updateObjectsInList(state.applicantsList, jobApplicationIds, { invitedForVetting: true, vettingStatus: AiInterviewStatusEnum.PENDING, status: ApplicationStatusEnum.PENDING });
                invalidateJobPostCache(state.jobId);

                state.countVetted = state.countVetted + jobApplicationIds.length;

                // kanbanColumnsCounts is now dynamic and updated via getVettingApplicantsListForKanbanView
                // Counts are managed per column using the total from API responses

            })
            .addCase(vetApplicantsBatch.rejected, (state, action) => {
                Toast.Error({ content: action.error?.message });
            })
            .addCase(vetApplicantBatchResultFinished, (state, action) => {
                const updatedApplicantsIds = action.payload?.updatedApplicantsIds;
                let updatedApplicantsList = [...state.applicantsList];
                for (const updatedApplicantId of updatedApplicantsIds) {
                    updatedApplicantsList = updateObjectInList(updatedApplicantsList, updatedApplicantId, { invitedForVetting: true, vettingStatus: AiInterviewStatusEnum.PENDING, status: ApplicationStatusEnum.INTERVIEWED_BY_SAUD });
                }
                state.applicantsList = updatedApplicantsList;
                state.countVetted = state.countVetted + updatedApplicantsIds.length;
                invalidateJobPostCache(state.jobId);

            })
            .addCase(fetchVideoInterview.pending, (state, action) => {
                state.isLoadingFetchingVideoInterview = true;
            })
            .addCase(fetchVideoInterview.fulfilled, (state, action) => {
                state.isLoadingFetchingVideoInterview = false;
                const videoRes = action?.payload;

                const selectedApplicant = state.selectedApplicant;
                const videoCv = selectedApplicant?.videoCv?.[0];
                if (selectedApplicant) {
                    state.selectedApplicant = {
                        ...selectedApplicant,
                        videoCv: [{
                            ...videoCv,
                            isVideoLocked: false,
                            url: videoRes?.url
                        }]
                    };

                    state.applicantsList = updateObjectInList(state.applicantsList, selectedApplicant.externalId, {
                        videoCv: [{
                            ...videoCv,
                            isVideoLocked: false,
                            url: videoRes?.url
                        }]
                    });

                    invalidateJobPostCache(state.jobId);
                }
            })
            .addCase(fetchVideoInterview.rejected, (state, action) => {
                state.isLoadingFetchingVideoInterview = false;
                Toast.Error({ content: action.error?.message });
            })
            .addCase(resetCountApplied, (state, action) => {
                state.countApplied = action.payload;
            })
            .addCase(setJustViewedChats, (state, action) => {
                state.justViewedChats = action.payload
            })
            .addCase(cancelInterviews.pending, (state, action) => {
            })
            .addCase(cancelInterviews.fulfilled, (state, action) => {
                Toast.Success({ content: t('interviews_cancelled') });
            })
            .addCase(cancelInterviews.rejected, (state, action) => {
                Toast.Error({ content: action.error?.message });
            })
            .addCase(rateInterview.pending, (state, action) => {
                state.isLoadingRateInterview = true;
            })
            .addCase(rateInterview.fulfilled, (state, action) => {
                state.isLoadingRateInterview = false;
                Toast.Success({ content: t('interview_rated') });
                const selectedApplicant = state.selectedApplicant;
                if (selectedApplicant) {
                    state.selectedApplicant = {
                        ...selectedApplicant,
                        partnerSubmittedFeedback: true,
                    }
                    state.applicantsList = updateObjectInList(state.applicantsList, selectedApplicant.externalId, { partnerSubmittedFeedback: true });
                }
            })
            .addCase(rateInterview.rejected, (state, action) => {
                state.isLoadingRateInterview = false;
                Toast.Error({ content: action.error?.message });
            })

            .addCase(getInterviewDetails.pending, (state, action) => {
                state.isLoadingInterviewDetails = true;
            })
            .addCase(getInterviewDetails.fulfilled, (state, action) => {
                state.isLoadingInterviewDetails = false;
                state.interviewDetails = action?.payload;
            })
            .addCase(getInterviewDetails.rejected, (state, action) => {
                state.isLoadingInterviewDetails = false;
                Toast.Error({ content: action.error?.message });
            })
            .addCase(sendBulkMessage.pending, (state, action) => {
                state.loadingSendBulkMsg = true;
            })
            .addCase(sendBulkMessage.fulfilled, (state, action) => {
                state.loadingSendBulkMsg = false;
            })
            .addCase(sendBulkMessage.rejected, (state, action) => {
                state.loadingSendBulkMsg = false;
                Toast.Error({ content: action.error?.message });
            })
            .addCase(setResetSelectedApplicantsIds, (state, action) => {
                state.resetSelectedApplicantsIds = action.payload;
            })

            .addCase(getVettingApplicantsListForKanbanView.pending, (state, action) => {
                // state.isLoadingVettingApplicantsListForKanbanView = true;
            })
            .addCase(getVettingApplicantsListForKanbanView.fulfilled, (state, action) => {
                // state.isLoadingVettingApplicantsListForKanbanView = false;
                const response = action?.payload;
                const applicants = response?.applicants;
                const newStatus = response?.newStatus;
                const isVettedBySaud = response?.isVettedBySaud;
                const columnId = response?.columnId;
                const skip = action?.meta?.arg?.filters?.skip || 0;
                const isInitialLoad = skip === 0;

                // Handle dynamic columns
                if (columnId && state.kanbanColumnsData[columnId]) {
                    const newList = [...state.kanbanColumnsData[columnId].applicants, ...applicants];
                    state.kanbanColumnsData[columnId].applicants = removeDuplicatesFromArrayOfObjects2(newList, 'externalId');
                    state.kanbanColumnsData[columnId].loading = false;

                    // Update count only on initial load (skip === 0) or when we've loaded all applicants (empty response)
                    // This prevents overwriting optimistic count updates during pagination
                    if (isInitialLoad && response?.total !== undefined) {
                        // Initial load: use API total
                        state.kanbanColumnsData[columnId].count = response.total;
                        state.kanbanColumnsCounts[columnId] = response.total;
                    } else if (applicants?.length === 0) {
                        // No more applicants to load: use actual length
                        state.kanbanColumnsData[columnId].count = state.kanbanColumnsData[columnId].applicants.length;
                        state.kanbanColumnsCounts[columnId] = state.kanbanColumnsData[columnId].applicants.length;
                    }
                    // For pagination (page > 0), don't overwrite the count - keep the optimistic update
                }

                // Legacy static columns handling (for backward compatibility)
                if (newStatus === ApplicationStatusEnum.PENDING && !isVettedBySaud) {
                    const newList = [...state.applicantsAppliedKanbanList, ...applicants];
                    state.applicantsAppliedKanbanList = removeDuplicatesFromArrayOfObjects2(newList, 'externalId');
                    if (applicants?.length === 0) {
                        state.applicantsAppliedKanbanListCount = state.applicantsAppliedKanbanList.length;
                    }
                } else if (newStatus === ApplicationStatusEnum.PENDING && isVettedBySaud) {
                    const newList = [...state.applicantsInterviewedKanbanList, ...applicants];
                    state.applicantsInterviewedKanbanList = removeDuplicatesFromArrayOfObjects2(newList, 'externalId');

                    if (applicants?.length === 0) {
                        state.applicantsInterviewedKanbanListCount = state.applicantsInterviewedKanbanList.length;
                    }
                } else if (newStatus === ApplicationStatusEnum.SHORT_LISTED) {
                    const newList = [...state.applicantsShortlistedKanbanList, ...applicants];
                    state.applicantsShortlistedKanbanList = removeDuplicatesFromArrayOfObjects2(newList, 'externalId');
                    if (applicants?.length === 0) {
                        state.applicantsShortlistedKanbanListCount = state.applicantsShortlistedKanbanList.length;
                    }
                } else if (newStatus === ApplicationStatusEnum.FINALIST) {
                    const newList = [...state.applicantsFinalistKanbanList, ...applicants];
                    state.applicantsFinalistKanbanList = removeDuplicatesFromArrayOfObjects2(newList, 'externalId');
                    if (applicants?.length === 0) {
                        state.applicantsFinalistKanbanListCount = state.applicantsFinalistKanbanList.length;
                    }
                }
            })
            .addCase(getVettingApplicantsListForKanbanView.rejected, (state, action) => {
                // state.isLoadingVettingApplicantsListForKanbanView = false;
                const columnId = action.meta.arg?.columnId;
                if (columnId && state.kanbanColumnsData[columnId]) {
                    state.kanbanColumnsData[columnId].loading = false;
                }
                Toast.Error({ content: action.error?.message });
            })
            .addCase(setReloadKanbanColumns, (state, action) => {
                state.reloadKanbanColumns = action.payload;
            })
            // Dynamic columns reducers
            .addCase(getKanbanColumns.pending, (state) => {
                state.isLoadingKanbanColumns = true;
            })
            .addCase(getKanbanColumns.fulfilled, (state, action) => {
                state.isLoadingKanbanColumns = false;
                const columns = action.payload;
                state.kanbanColumns = columns.sort((a, b) => a.order - b.order);

                // Initialize column data if not exists
                columns.forEach(column => {
                    if (!state.kanbanColumnsData[column.id]) {
                        state.kanbanColumnsData[column.id] = {
                            applicants: [],
                            count: 0,
                            page: 0,
                            loading: false,
                        };
                    }
                    // Initialize kanbanColumnsCounts if not exists
                    if (state.kanbanColumnsCounts[column.id] === undefined) {
                        state.kanbanColumnsCounts[column.id] = 0;
                    }
                });
            })
            .addCase(getKanbanColumns.rejected, (state) => {
                state.isLoadingKanbanColumns = false;
            })
            .addCase(setKanbanColumnPage, (state, action) => {
                const { columnId, page } = action.payload;
                if (state.kanbanColumnsData[columnId]) {
                    state.kanbanColumnsData[columnId].page = page;
                }
            })
            .addCase(setKanbanColumnLoading, (state, action) => {
                const { columnId, loading } = action.payload;
                if (state.kanbanColumnsData[columnId]) {
                    state.kanbanColumnsData[columnId].loading = loading;
                }
            })
            .addCase(setKanbanColumnCount, (state, action) => {
                const { columnId, count } = action.payload;
                if (state.kanbanColumnsData[columnId]) {
                    state.kanbanColumnsData[columnId].count = count;
                }
                // Also update kanbanColumnsCounts
                state.kanbanColumnsCounts[columnId] = count;
            })
            .addCase(setKanbanColumnApplicants, (state, action) => {
                const { columnId, applicants } = action.payload;
                if (state.kanbanColumnsData[columnId]) {
                    state.kanbanColumnsData[columnId].applicants = applicants;
                }
            })
            .addCase(updateApplicantsScheduledInterviewsInKanbanColumns, (state, action) => {
                const updatedApplicants = action.payload as ApplicantListItem[];
                if (!updatedApplicants?.length || !state.kanbanColumnsData) return;
                Object.keys(state.kanbanColumnsData).forEach(columnId => {
                    const column = state.kanbanColumnsData[columnId];
                    if (column?.applicants?.length) {
                        column.applicants = column.applicants.map(app => {
                            const updated = updatedApplicants.find(a => a.externalId === app.externalId);
                            return updated ?? app;
                        });
                    }
                });
            })
            .addCase(resetKanbanColumnsData, (state) => {
                Object.keys(state.kanbanColumnsData).forEach(columnId => {
                    state.kanbanColumnsData[columnId] = {
                        applicants: [],
                        count: 0,
                        page: 0,
                        loading: false,
                    };
                });
            })
            // Dynamic application statuses reducers
            .addCase(getApplicationStatuses.pending, (state) => {
                state.isLoadingApplicationStatuses = true;
            })
            .addCase(getApplicationStatuses.fulfilled, (state, action) => {
                state.isLoadingApplicationStatuses = false;
                const statuses = action.payload;
                state.applicationStatuses = statuses.sort((a, b) => a.order - b.order);
            })
            .addCase(getApplicationStatuses.rejected, (state) => {
                state.isLoadingApplicationStatuses = false;
            })
            .addCase(setApplicantsAppliedKanbanListPage, (state, action) => {
                state.applicantsAppliedKanbanListPage = action.payload;
            })
            .addCase(setApplicantsInterviewedKanbanListPage, (state, action) => {
                state.applicantsInterviewedKanbanListPage = action.payload;
            })
            .addCase(setApplicantsShortlistedKanbanListPage, (state, action) => {
                state.applicantsShortlistedKanbanListPage = action.payload;
            })
            .addCase(setApplicantsFinalistKanbanListPage, (state, action) => {
                state.applicantsFinalistKanbanListPage = action.payload;
            })
            .addCase(setApplicantsAppliedKanbanListLoading, (state, action) => {
                state.applicantsAppliedKanbanListLoading = action.payload;
            })
            .addCase(setApplicantsInterviewedKanbanListLoading, (state, action) => {
                state.applicantsInterviewedKanbanListLoading = action.payload;
            })
            .addCase(setApplicantsShortlistedKanbanListLoading, (state, action) => {
                state.applicantsShortlistedKanbanListLoading = action.payload;
            })
            .addCase(setApplicantsFinalistKanbanListLoading, (state, action) => {
                state.applicantsFinalistKanbanListLoading = action.payload;
            })
            .addCase(setApplicantsAppliedKanbanList, (state, action) => {
                state.applicantsAppliedKanbanList = action.payload;
            })
            .addCase(setApplicantsInterviewedKanbanList, (state, action) => {
                state.applicantsInterviewedKanbanList = action.payload;
            })
            .addCase(setApplicantsShortlistedKanbanList, (state, action) => {
                state.applicantsShortlistedKanbanList = action.payload;
            })
            .addCase(setApplicantsFinalistKanbanList, (state, action) => {
                state.applicantsFinalistKanbanList = action.payload;
            })
            .addCase(setApplicantsToMoveBetweenKanbanColumns, (state, action) => {
                state.applicantsToMoveBetweenKanbanColumns = action.payload;
            })
            .addCase(setApplicantsAppliedKanbanListCount, (state, action) => {
                state.applicantsAppliedKanbanListCount = action.payload;
            })
            .addCase(setApplicantsInterviewedKanbanListCount, (state, action) => {
                state.applicantsInterviewedKanbanListCount = action.payload;
            })
            .addCase(setApplicantsShortlistedKanbanListCount, (state, action) => {
                state.applicantsShortlistedKanbanListCount = action.payload;
            })
            .addCase(setApplicantsFinalistKanbanListCount, (state, action) => {
                state.applicantsFinalistKanbanListCount = action.payload;
            })
            .addCase(getApplicantNotes.pending, (state) => {
                state.isLoadingApplicantNotes = true;
            })
            .addCase(getApplicantNotes.fulfilled, (state, action) => {
                state.isLoadingApplicantNotes = false;
                state.applicantNotes = action.payload;
            })
            .addCase(getApplicantNotes.rejected, (state, action) => {
                state.isLoadingApplicantNotes = false;
                Toast.Error({ content: action.error.message });
            })
            .addCase(createApplicantNote.pending, (state) => {
                state.isSavingApplicantNote = true;
            })
            .addCase(createApplicantNote.fulfilled, (state, action) => {
                state.isSavingApplicantNote = false;
                const note = action.payload;
                state.applicantNotes = [{ externalId: note.noteExternalId, note: note.note, createdAt: note.createdAt, ownerName: note.ownerName, ownerExternalId: note.ownerExternalId }, ...state.applicantNotes];
                // Toast.Success({ content: t('note_created') });
            })
            .addCase(createApplicantNote.rejected, (state, action: any) => {
                state.isSavingApplicantNote = false;
                Toast.Error({ content: action.payload?.message || action.error.message });
            })
            .addCase(updateApplicantNote.pending, (state) => {
                state.isSavingApplicantNote = true;
            })
            .addCase(updateApplicantNote.fulfilled, (state, action) => {
                state.isSavingApplicantNote = false;
                const updatedNote = action.payload;
                // Match by externalId and update the note with new data
                state.applicantNotes = state.applicantNotes.map((n) =>
                    n.externalId === updatedNote.externalId
                        ? {
                            ...n,
                            note: updatedNote.note,
                            createdAt: updatedNote.createdAt,
                            updatedAt: updatedNote.updatedAt,
                            ownerName: updatedNote.ownerName,
                            ownerExternalId: updatedNote.ownerExternalId
                        }
                        : n
                );
                // Toast.Success({ content: t('note_updated') });
            })
            .addCase(updateApplicantNote.rejected, (state, action: any) => {
                state.isSavingApplicantNote = false;
                Toast.Error({ content: action.payload?.message || action.error.message });
            })
            .addCase(deleteApplicantNote.pending, (state) => {
                state.isSavingApplicantNote = true;
            })
            .addCase(deleteApplicantNote.fulfilled, (state, action) => {
                state.isSavingApplicantNote = false;
                // Use noteId from the original request arguments
                const noteId = action.meta.arg.noteId;
                state.applicantNotes = (state.applicantNotes || []).filter((n) => n.externalId !== noteId);
                // Toast.Success({ content: t('note_deleted') });
            })
            .addCase(deleteApplicantNote.rejected, (state, action) => {
                state.isSavingApplicantNote = false;
                Toast.Error({ content: action.error.message });
            })
            .addCase(setSelectedApplicantNote, (state, action) => {
                state.selectedApplicantNote = action.payload;
            })
            .addCase(manageApplicantRejectionReason.pending, (state, action) => {
                // Optional: Add loading state if needed
            })
            .addCase(manageApplicantRejectionReason.fulfilled, (state, action) => {
                // Success handled in component with Toast
            })
            .addCase(manageApplicantRejectionReason.rejected, (state, action) => {
                Toast.Error({ content: action.error?.message || t('error_managing_rejection_reason') });
            })

            // Dynamic application statuses reducers
            .addCase(getJobDynamicStages.pending, (state, action) => {
                // Avoid full-list spinner flash when refreshing after upsert
                if (state.jobDynamicStages.length === 0) {
                    state.isLoadingJobDynamicStages = true;
                }
            })
            .addCase(getJobDynamicStages.fulfilled, (state, action) => {
                state.jobDynamicStages = action.payload;
                state.isLoadingJobDynamicStages = false;
            })
            .addCase(getJobDynamicStages.rejected, (state, action) => {
                state.isLoadingJobDynamicStages = false;
                Toast.Error({ content: action?.error?.message })
            })
            .addCase(upsertJobDynamicStages.pending, (state, action) => {
                state.isLoadingUpsertJobDynamicStages = true;
            })
            .addCase(upsertJobDynamicStages.fulfilled, (state, action) => {
                state.isLoadingUpsertJobDynamicStages = false;
                Toast.Success({ content: t('dynamic_stages_updated') });
                invalidateJobPostCache(state.jobId);
            })
            .addCase(upsertJobDynamicStages.rejected, (state, action) => {
                state.isLoadingUpsertJobDynamicStages = false;
                Toast.Error({ content: action?.error?.message })
            })
            .addCase(deleteJobDynamicStage.pending, (state, action) => {
                state.isLoadingDeleteJobDynamicStages = true;
            })
            .addCase(deleteJobDynamicStage.fulfilled, (state, action) => {
                state.isLoadingDeleteJobDynamicStages = false;
                // Toast.Success({ content: t('dynamic_stage_deleted') });
                invalidateJobPostCache(state.jobId);
            })
            .addCase(deleteJobDynamicStage.rejected, (state, action) => {
                state.isLoadingDeleteJobDynamicStages = false;
                // Toast.Error({ content: action?.error?.message })
            })
            .addCase(sendDataCollectionRequests.fulfilled, (state, action) => {
                // Invalidate cache and trigger refetch since applicant statuses changed to ONBOARDING
                invalidateJobPostCache(state.jobId);
                // Success/error toasts are shown by callers (sidebar / bulk actions) to avoid duplicate toasts
            })
            .addCase(cancelDataCollectionRequests.fulfilled, (state) => {
                invalidateJobPostCache(state.jobId);
                Toast.Success({ content: t('onboarding_request_cancelled') });
            })
            .addCase(cancelDataCollectionRequests.rejected, (_state, action) => {
                Toast.Error({ content: action?.error?.message || t('error_cancelling_onboarding_request') });
            })
            .addCase(resendDataCollectionRequests.fulfilled, (state) => {
                invalidateJobPostCache(state.jobId);
                Toast.Success({ content: t('onboarding_request_resent') });
            })
            .addCase(resendDataCollectionRequests.rejected, (_state, action) => {
                Toast.Error({ content: action?.error?.message || t('error_resending_onboarding_request') });
            })
            .addCase(recollectTask.fulfilled, (state) => {
                invalidateJobPostCache(state.jobId);
                Toast.Success({ content: t('onboarding_recollect_success') });
            })
            .addCase(recollectTask.rejected, (_state, action) => {
                Toast.Error({ content: action?.error?.message || t('error_recollect_task') });
            })
            .addCase(syncApplicantsToTalentAcquisition.pending, (state) => {
                state.isLoadingTalentAcquisitionSync = true;
            })
            .addCase(syncApplicantsToTalentAcquisition.fulfilled, (state, action) => {
                state.isLoadingTalentAcquisitionSync = false;
                const payload = action.payload;
                if (!payload)
                    return;
                const syncedIds = new Set(
                    (payload?.results || [])
                        .filter(result => result.success && (result.skipped ? result.skipReason === 'ALREADY_SYNCED' : !!result.applicationNum))
                        .map(result => result.applicationId)
                );
                const applySync = (applicant: ApplicantListItem) => {
                    if (!syncedIds.has(applicant.externalId))
                        return applicant;
                    const result = payload.results.find(item => item.applicationId === applicant.externalId);
                    return {
                        ...applicant,
                        talentAcquisitionSync: {
                            isSynced: true,
                            applicationNum: result?.applicationNum,
                            lastSyncedAt: Date.now(),
                            lastStatus: 'SUCCESS',
                        },
                    };
                };
                state.applicantsList = state.applicantsList.map(applySync);
                state.selectedApplicants = state.selectedApplicants.map(applySync);
                if (state.selectedApplicant)
                    state.selectedApplicant = applySync(state.selectedApplicant);
                if (state.kanbanColumnsData) {
                    Object.keys(state.kanbanColumnsData).forEach(columnId => {
                        const column = state.kanbanColumnsData[columnId];
                        if (column?.applicants?.length)
                            state.kanbanColumnsData[columnId] = { ...column, applicants: column.applicants.map(applySync) };
                    });
                }

                if (payload.failedCount > 0) {
                    Toast.Error({ content: t('talent_acquisition_sync_partial_failed', { value: payload.failedCount }) });
                } else if (payload.successCount > 0) {
                    Toast.Success({
                        content: payload.successCount === 1
                            ? t('talent_acquisition_sync_success_one')
                            : t('talent_acquisition_sync_success'),
                    });
                    state.selectedApplicants = [];
                } else if (payload.skippedCount > 0) {
                    Toast.Success({ content: t('talent_acquisition_already_synced') });
                    state.selectedApplicants = [];
                }
            })
            .addCase(syncApplicantsToTalentAcquisition.rejected, (state, action: any) => {
                state.isLoadingTalentAcquisitionSync = false;
                Toast.Error({ content: action.payload?.message || action.error?.message || t('talent_acquisition_sync_failed') });
            })
    }
})

export default appApplicantsSlice.reducer
